/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useState, useEffect, Fragment, useRef } from "react";
import { API, Auth, graphqlOperation } from "aws-amplify";
import { createUsedTools, updateUsedTools } from "src/graphql/mutations";
import { Dialog, Transition } from "@headlessui/react";
import toast from "react-hot-toast";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import ja from "date-fns/locale/ja";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";

import "react-datepicker/dist/react-datepicker.css";

import { queryCalibrations } from "src/services/AmplifyQuery";

/**
 * The AddUsedToolModal component
 *
 * @author Thais Watanabe
 */
const AddUsedToolModal = (props) => {
  const {
    setIsOpen,
    isOpen,
    tools,
    setTools,
    selectedTool,
    setSelectedTool,
    inspection,
    turbineInspections,
  } = props;
  const [loggedUser, setLoggedUser] = useState("");
  const [calibrations, setCalibrations] = useState([]);
  const [selectedCalibration, setSelectedCalibration] = useState(null);
  const [selectedTurbines, setSelectedTurbines] = useState(null);
  const [error, setError] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const cancelButtonRef = useRef(null);

  registerLocale("ja", ja);

  /* useEffect */
  useEffect(() => {
    fetchCalibrations();
  }, [calibrations]);

  /* useEffect */
  useEffect(() => {
    if (selectedTool) {
      setSelectedCalibration({
        value: selectedTool.calibration,
        label: selectedTool.calibration.serialNumber,
      });

      setStartDate(new Date(selectedTool.dateStart));
      setEndDate(new Date(selectedTool.dateEnd));

      const turbines = selectedTool.turbines.split(",");
      const turbinesObjs = turbines?.map((turbine) => ({
        value: turbine,
        label: turbine,
      }));
      setSelectedTurbines(turbinesObjs);
    }
  }, [selectedTool]);

  /* useEffect */
  useEffect(() => {
    Auth.currentUserInfo()
      .then((result) => {
        setLoggedUser(result.username);
      })
      .catch((error) => console.log(error));
  }, []);

  /**
   * fetchTools
   */
  async function fetchCalibrations() {
    queryCalibrations(null, setCalibrations);
  }

  /**
   * submit
   */
  async function submit() {
    var error = false;

    if (!selectedCalibration || !startDate || !endDate || !selectedTurbines) {
      setError(true);
      error = true;
    } else {
      setError(false);
      error = false;
    }

    if (!error) {
      if (selectedTool) {
        update();
      } else {
        create();
      }
    }
  }

  /**
   * update
   */
  async function update() {
    try {
      selectedTool.calibrationID = selectedCalibration.value.id;
      selectedTool.dateStart = moment(startDate).format("YYYY-MM-DD");
      selectedTool.dateEnd = moment(endDate).format("YYYY-MM-DD");
      selectedTool.turbines = selectedTurbines.map((obj) => obj.value).join(",");

      const input = { ...selectedTool };
      delete input._deleted;
      delete input._lastChangedAt;
      delete input.calibration;

      await API.graphql(graphqlOperation(updateUsedTools, { input: input }));

      setStartDate(null);
      setEndDate(null);
      setIsOpen(!isOpen);

      toast.success("保存しました");
    } catch (err) {
      console.log(err);
      toast.error("エラーが発生しました");
    }
  }

  /**
   * addCalibration
   */
  async function create() {
    try {
      const input = {
        id: uuidv4(),
        calibrationID: selectedCalibration.value.id,
        inspectionID: inspection.id,
        dateStart: moment(startDate).format("YYYY-MM-DD"),
        dateEnd: moment(endDate).format("YYYY-MM-DD"),
        turbines: selectedTurbines.map((obj) => obj.value).join(","),
        author: loggedUser,
      };

      const result = await API.graphql(
        graphqlOperation(createUsedTools, { input: input })
      );

      const newUsedTool = result.data.createUsedTools;

      setStartDate(null);
      setEndDate(null);
      setIsOpen(!isOpen);

      setTools([...tools, newUsedTool]);
      toast.success("保存しました");
    } catch (err) {
      console.log(err);
      toast.error("エラーが発生しました");
    }
  }

  /**
   * closeModal
   */
  function closeModal() {
    setIsOpen(!isOpen);
    setStartDate(null);
    setEndDate(null);
    setSelectedTool(null);
    setSelectedCalibration(null);
    setSelectedTurbines(null);
  }

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-visible"
        initialFocus={cancelButtonRef}
        onClose={closeModal}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                  onClick={() => closeModal()}
                >
                  <span className="sr-only">Close</span>
                  {/* <!-- Heroicon name: x --> */}
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <form>
                <div>
                  <div>
                    <h2 className="text-lg leading-6 font-medium text-gray-900">
                      {selectedTool ? "使用工具修正" : "新規使用工具登録"}
                    </h2>
                  </div>

                  <div className="grid mt-6 grid-cols-4 gap-x-4 sm:grid-cols-4">
                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="calibrationID"
                        className="block text-sm font-medium text-gray-700"
                      >
                        管理番号 *
                      </label>
                      <Select
                        id="calibrationID"
                        name="calibrationID"
                        options={calibrations.map((obj) => ({
                          value: obj,
                          label: obj.serialNumber,
                        }))}
                        value={selectedCalibration}
                        onChange={(event) => setSelectedCalibration(event)}
                        placeholder="選択してください"
                        theme={(theme) => ({
                          ...theme,
                          colors: {
                            ...theme.colors,
                            primary: "#0D9488",
                          },
                        })}
                        styles={{
                          input: (base) => ({
                            ...base,
                            "input:focus": {
                              boxShadow: "none",
                            },
                          }),
                        }}
                      />
                      {error && selectedCalibration?.length < 1 && (
                        <p className="mt-2 text-sm text-red-600">
                          「管理番号」を入力してください
                        </p>
                      )}
                    </div>

                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="tool"
                        className="block text-sm font-medium text-gray-700"
                      >
                        機器名称
                      </label>
                      <input
                        value={selectedCalibration?.value?.tool?.name}
                        type="text"
                        name="tool"
                        id="tool"
                        disabled={true}
                        className={`${"bg-gray-100 text-gray-600 focus:ring-teal-500 focus:border-teal-500 border-gray-300"} block w-full shadow-sm sm:text-sm rounded-md`}
                      />
                    </div>

                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="maker"
                        className="block text-sm font-medium text-gray-700"
                      >
                        メーカー
                      </label>
                      <input
                        value={selectedCalibration?.value?.maker?.name}
                        type="text"
                        name="maker"
                        id="maker"
                        disabled={true}
                        className={`${"bg-gray-100 text-gray-600 focus:ring-teal-500 focus:border-teal-500 border-gray-300"} block w-full shadow-sm sm:text-sm rounded-md`}
                      />
                    </div>

                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="modelNumber"
                        className="block text-sm font-medium text-gray-700"
                      >
                        型式番号
                      </label>
                      <input
                        value={selectedCalibration?.value?.modelNumber}
                        type="text"
                        name="modelNumber"
                        id="modelNumber"
                        disabled={true}
                        className={`${"bg-gray-100 text-gray-600 focus:ring-teal-500 focus:border-teal-500 border-gray-300"} block w-full shadow-sm sm:text-sm rounded-md`}
                      />
                    </div>

                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="serialNumber"
                        className="block text-sm font-medium text-gray-700"
                      >
                        シリアル番号
                      </label>
                      <input
                        value={selectedCalibration?.value?.serialNumber}
                        type="text"
                        name="serialNumber"
                        id="serialNumber"
                        disabled={true}
                        className={`${"bg-gray-100 text-gray-600 focus:ring-teal-500 focus:border-teal-500 border-gray-300"} block w-full shadow-sm sm:text-sm rounded-md`}
                      />
                    </div>

                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="expirationDate"
                        className="block text-sm font-medium text-gray-700"
                      >
                        校正有効期限
                      </label>
                      <input
                        value={selectedCalibration?.value?.expirationDate}
                        type="text"
                        name="expirationDate"
                        id="expirationDate"
                        disabled={true}
                        className={`${"bg-gray-100 text-gray-600 focus:ring-teal-500 focus:border-teal-500 border-gray-300"} block w-full shadow-sm sm:text-sm rounded-md`}
                      />
                    </div>

                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="expirationDate"
                        className="block text-sm font-medium text-gray-700"
                      >
                        使用前点検日 *
                      </label>
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        dateFormat="yyyy/MM/dd"
                        locale="ja"
                        className="focus:ring-teal-500 focus:border-teal-500 border-gray-300 block w-full shadow-sm sm:text-sm rounded-md"
                      />
                    </div>

                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="expirationDate"
                        className="block text-sm font-medium text-gray-700"
                      >
                        使用後点検日 *
                      </label>
                      <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        dateFormat="yyyy/MM/dd"
                        locale="ja"
                        className="focus:ring-teal-500 focus:border-teal-500 border-gray-300 block w-full shadow-sm sm:text-sm rounded-md"
                      />
                    </div>

                    <div className="py-2 sm:col-span-4">
                      <label
                        htmlFor="turbines"
                        className="block text-sm font-medium text-gray-700"
                      >
                        使用号機 *
                      </label>
                      <Select
                        id="turbines"
                        name="turbines"
                        isMulti
                        closeMenuOnSelect={false}
                        options={[
                          { value: "全機", label: "全機" },
                          ...turbineInspections.map((obj) => ({
                            value: obj.windTurbine.tower.toString(),
                            label: obj.windTurbine.tower.toString(),
                          })),
                        ]}
                        value={selectedTurbines}
                        onChange={(event) => setSelectedTurbines(event)}
                        placeholder="選択してください"
                        theme={(theme) => ({
                          ...theme,
                          colors: {
                            ...theme.colors,
                            primary: "#0D9488",
                          },
                        })}
                        styles={{
                          input: (base) => ({
                            ...base,
                            "input:focus": {
                              boxShadow: "none",
                            },
                          }),
                        }}
                      />
                      {error && selectedTurbines.length < 1 && (
                        <p className="mt-2 text-sm text-red-600">
                          「使用号機」を入力してください
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    onClick={() => submit()}
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-teal-600 text-base font-medium text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:col-start-2 sm:text-sm"
                  >
                    保存する
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => closeModal()}
                  >
                    キャンセル
                  </button>
                </div>
              </form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AddUsedToolModal;
